/**
 * 오답·근거 다시 풀기. 원문 순서·문맥을 그대로 보여 주고, GR과 근거를 모두 통과한 지점만 정답이 채워진 읽기전용으로 잠근다.
 * 통과 여부와 완료는 서버(GET /attempts/:id/review)가 계산한다. 이 화면은 판단하지 않고 표시한다.
 * 근거 통과는 객관식 근거 문제의 복습 회차 응답으로만 이뤄지며(재배정 불필요, 최초 근거 응답은 불변), 이 화면의 읽기·클릭은 근거 통과가 아니다.
 * 근거 문제가 없는 지점은 "근거 자료 없음"으로만 표시하고 문제를 만들지 않는다.
 * 같은 지점을 같은 선택으로 다시 보낼 때는 같은 요청 키를 쓰고, 선택이 바뀌면 새 키를 만든다.
 */
import { h, clear } from './dom.js';
import { currentGen, newRequestKey, type Transport } from '../core/transport.js';
import type { ItemsResponse } from '../core/types.js';
import { toggleButton } from './toggle-button.js';

interface ReviewPoint {
  pointId: string; displayNumber: string;
  gr: { state: string; firstResult: 'correct' | 'incorrect' | null; passed: boolean; reviewRounds: number };
  evidence: { state: 'passed' | 'failed' | 'open' | 'unavailable'; question: { stem: string; options: { id: string; no: number; text: string }[] } | null };
  locked: boolean; needsGr: boolean; needsEvidence: boolean; passedSelectedOptionIds: string[] | null;
}
interface Review { status: string; complete: boolean; evidenceUnavailableCount: number; remainingCount: number; lockedCount: number; points: ReviewPoint[] }

export const EVIDENCE_NOTE: Record<ReviewPoint['evidence']['state'], string> = {
  passed: '근거 확인 통과', failed: '근거 문제를 틀렸어요. 다시 풀어 보세요', open: '근거 문제를 풀어야 해요',
  unavailable: '근거 자료 없음',
};

export function statusLine(r: Pick<Review, 'status' | 'evidenceUnavailableCount' | 'remainingCount'>): string {
  if (r.status === 'complete') return '모든 지점을 통과했어요.';
  if (r.status === 'complete_evidence_unavailable') return `제공된 문제 학습 완료, 근거 확인 불가 항목 ${r.evidenceUnavailableCount}개`;
  if (r.status === 'pending') return '결과 대기 중인 지점이 있어요.';
  return `아직 통과하지 못한 지점 ${r.remainingCount}개`;
}

export function mountReview(root: HTMLElement, t: Transport, attemptId: string, handlers: { onBack: () => void; onEvidence: () => void }): void {
  const gen = currentGen();
  const live = () => gen === currentGen() && root.isConnected;
  const msg = h('p', { role: 'status', 'data-testid': 'review-msg' });
  const body = h('div', { 'data-testid': 'review-body' });
  clear(root);
  root.append(h('div', { class: 'lhead' }, h('h2', {}, '오답·근거 다시 풀기'), h('button', { class: 'secondary', 'data-testid': 'review-back', onclick: handlers.onBack }, '결과로 돌아가기')), msg, body);
  // 지점별 제출 피드백: 해당 지점 단추 근처(aria-live)에 보이고, 재조회로 다시 그려도 남는다. 긴 지문에서 맨 위 msg만 쓰면 화면 밖이 된다
  const pointFb = new Map<string, { text: string; ok: boolean }>();
  const fbSpans = new Map<string, HTMLElement>();
  function setFb(pointId: string, text: string, ok: boolean): void {
    pointFb.set(pointId, { text, ok });
    const s = fbSpans.get(pointId);
    if (s !== undefined && s.isConnected) { s.textContent = text; s.dataset['ok'] = ok ? 'true' : 'false'; }
  }
  function fbNode(pointId: string): HTMLElement {
    const f = pointFb.get(pointId);
    const s = h('span', { class: 'pt-fb', role: 'status', 'aria-live': 'polite', 'data-testid': `review-fb-${pointId}`, 'data-ok': f === undefined ? '' : f.ok ? 'true' : 'false' }, f?.text ?? '');
    fbSpans.set(pointId, s);
    return s;
  }
  const drafts = new Map<string, Set<string>>();
  const keys = new Map<string, { sig: string; key: string }>();
  let items: ItemsResponse | null = null;
  let review: Review | null = null;

  async function load(keepMsg = false): Promise<void> {
    const [i, r] = await Promise.all([t.request('GET', `/attempts/${attemptId}/items`), t.request('GET', `/attempts/${attemptId}/review`)]);
    if (!live()) return;
    if (i.kind !== 'http' || i.status !== 200 || r.kind !== 'http' || r.status !== 200) {
      // 조회 실패는 빈 목록·결과 대기와 다르고, 지난 표시는 숨긴다
      items = null; review = null; clear(body); msg.textContent = '복습 상태를 불러오지 못했어요. 결과 대기가 아니라 받아 오지 못한 상태예요.';
      body.append(h('button', { class: 'primary', 'data-testid': 'review-retry', onclick: () => void load() }, '다시 불러오기'));
      return;
    }
    items = i.body as unknown as ItemsResponse; review = r.body as unknown as Review; if (!keepMsg) msg.textContent = '';
    draw();
  }

  async function submit(p: ReviewPoint, btn: HTMLButtonElement): Promise<void> {
    const ids = [...(drafts.get(p.pointId) ?? [])].sort();
    if (ids.length === 0) { msg.textContent = `${p.displayNumber}번: 보기를 골라 주세요.`; setFb(p.pointId, '보기를 골라 주세요.', false); return; }
    const sig = ids.join(',');
    const prev = keys.get(p.pointId);
    const key = prev !== undefined && prev.sig === sig ? prev.key : newRequestKey();
    keys.set(p.pointId, { sig, key });
    btn.disabled = true;
    const r = await t.request('POST', `/attempts/${attemptId}/review/points/${p.pointId}/answer`, { key, body: { selectedOptionIds: ids } });
    if (!live()) return;
    if (r.kind !== 'http') { btn.disabled = false; msg.textContent = '제출됐는지 확인하지 못했어요. 같은 답으로 다시 눌러 주세요.'; setFb(p.pointId, '제출됐는지 확인하지 못했어요. 같은 답으로 다시 눌러 주세요.', false); return; }
    keys.delete(p.pointId); drafts.delete(p.pointId);
    msg.textContent = r.status === 200 ? (r.body['result'] === 'correct' ? `${p.displayNumber}번: 맞았어요.` : `${p.displayNumber}번: 틀렸어요. 다시 풀어 보세요.`) : `${p.displayNumber}번: 제출하지 못했어요. 상태를 다시 불러와요.`;
    setFb(p.pointId, r.status === 200 ? (r.body['result'] === 'correct' ? '맞았어요.' : '틀렸어요. 다시 풀어 보세요.') : '제출하지 못했어요. 상태를 다시 불러와요.', r.status === 200 && r.body['result'] === 'correct');
    await load(true);
  }

  const evDrafts = new Map<string, string>();
  const evKeys = new Map<string, { sig: string; key: string }>();

  async function submitEvidence(p: ReviewPoint, btn: HTMLButtonElement): Promise<void> {
    const sel = evDrafts.get(p.pointId);
    if (sel === undefined) { msg.textContent = `${p.displayNumber}번: 근거 문제의 보기를 골라 주세요.`; setFb(p.pointId, '근거 문제의 보기를 골라 주세요.', false); return; }
    const prev = evKeys.get(p.pointId);
    const key = prev !== undefined && prev.sig === sel ? prev.key : newRequestKey();
    evKeys.set(p.pointId, { sig: sel, key });
    btn.disabled = true;
    const r = await t.request('POST', `/attempts/${attemptId}/review/points/${p.pointId}/evidence-answer`, { key, body: { selectedOptionId: sel } });
    if (!live()) return;
    if (r.kind !== 'http') { btn.disabled = false; msg.textContent = '제출됐는지 확인하지 못했어요. 같은 답으로 다시 눌러 주세요.'; setFb(p.pointId, '제출됐는지 확인하지 못했어요. 같은 답으로 다시 눌러 주세요.', false); return; }
    evKeys.delete(p.pointId); evDrafts.delete(p.pointId);
    msg.textContent = r.status === 200 ? (r.body['result'] === 'correct' ? `${p.displayNumber}번 근거: 맞았어요.` : `${p.displayNumber}번 근거: 틀렸어요. 다시 풀어 보세요.`) : `${p.displayNumber}번 근거: 제출하지 못했어요. 상태를 다시 불러와요.`;
    setFb(p.pointId, r.status === 200 ? (r.body['result'] === 'correct' ? '근거: 맞았어요.' : '근거: 틀렸어요. 다시 풀어 보세요.') : '근거: 제출하지 못했어요. 상태를 다시 불러와요.', r.status === 200 && r.body['result'] === 'correct');
    await load(true);
  }

  /** 근거 문제(기존 객관식 검증 문제) 다시 풀기. 업로드 해설을 읽는 것은 근거 통과가 아니다 */
  function evidenceBox(p: ReviewPoint): HTMLElement | null {
    const q = p.evidence.question;
    if (!p.needsEvidence || q === null) return null;
    const box = h('span', { class: 'toggle-group', role: 'group', 'aria-label': `${p.displayNumber}번 근거 문제 보기` });
    const go = h('button', { class: 'primary', type: 'button', 'data-testid': `review-ev-submit-${p.pointId}` }, '근거 제출');
    go.onclick = () => void submitEvidence(p, go);
    const redraw = () => {
      clear(box);
      for (const o of q.options) {
        box.append(toggleButton(o.text, evDrafts.get(p.pointId) === o.id, `review-ev-opt-${o.id}`, () => { evDrafts.set(p.pointId, o.id); redraw(); }));
      }
    };
    redraw();
    return h('span', { class: 'ev-box', 'data-testid': `review-ev-box-${p.pointId}` }, h('span', { class: 'ev-stem' }, q.stem), box, go);
  }

  function draw(): void {
    clear(body);
    if (items === null || review === null) return;
    const byId = new Map(review.points.map((p) => [p.pointId, p]));
    body.append(h('p', { class: 'rstat', 'data-testid': 'review-status', 'data-state': review.status }, statusLine(review)));
    for (const it of items.items.filter((x) => x.responseMode === 'gr-points').sort((a, b) => a.order - b.order)) {
      const para = h('p', { class: 'rgr', 'data-testid': `review-item-${it.id}` });
      for (const s of it.segments ?? []) {
        if (s.kind === 'text') { para.append(s.text); continue; }
        const p = byId.get(s.pointId);
        const view = it.grPoints.find((g) => g.id === s.pointId);
        if (p === undefined || view === undefined) continue;
        para.append(pointView(p, view));
      }
      body.append(h('section', { class: 'item' }, h('div', { class: 'item-h' }, h('span', { class: 'no' }, `${it.number}번`)), para));
    }
  }

  function pointView(p: ReviewPoint, view: ItemsResponse['items'][number]['grPoints'][number]): HTMLElement {
    const label = (id: string) => view.options.find((o) => o.id === id)?.label ?? '';
    const note = h('span', { class: 'ev-note', 'data-testid': `review-ev-${p.pointId}` }, EVIDENCE_NOTE[p.evidence.state]);
    if (p.locked || !p.needsGr) {
      // 읽기전용: 정답(통과한 선택) 전부를 채워서 보여 준다. 근거가 남았으면 근거 문제로 안내
      const text = (p.passedSelectedOptionIds ?? []).map(label).join(', ');
      const ev = evidenceBox(p);
      return h('span', { class: ev === null ? 'rp' : 'rp rp-open', 'data-r': 'correct', 'data-locked': p.locked ? 'true' : 'false', 'data-testid': `review-pt-${p.pointId}` },
        h('span', { class: 'pt-no' }, p.displayNumber), h('span', { class: 'sel' }, text === '' ? '결과 대기' : text), note,
        fbNode(p.pointId), ev);
    }
    const draft = drafts.get(p.pointId) ?? new Set<string>();
    drafts.set(p.pointId, draft);
    const multiple = items?.pointModes?.[p.pointId] === 'multiple';
    const box = h('span', { class: 'toggle-group', role: 'group', 'aria-label': `${p.displayNumber}번 보기` });
    const go = h('button', { class: 'primary', type: 'button', 'data-testid': `review-submit-${p.pointId}` }, '이 지점 다시 제출');
    go.onclick = () => void submit(p, go);
    const redraw = () => {
      clear(box);
      for (const o of view.options) {
        box.append(toggleButton(o.label, draft.has(o.id), `review-opt-${o.id}`, () => {
          if (draft.has(o.id)) draft.delete(o.id); else { if (!multiple) draft.clear(); draft.add(o.id); }
          redraw();
        }));
      }
    };
    redraw();
    return h('span', { class: 'rp rp-open', 'data-r': 'incorrect', 'data-testid': `review-pt-${p.pointId}` }, h('span', { class: 'pt-no' }, p.displayNumber), box, go, fbNode(p.pointId), note, evidenceBox(p));
  }

  clear(body); body.append(h('p', { class: 'loading' }, '불러오고 있어요'));
  void load();
}
